/**
 * Telegram WebApp helpers. Safe to import in a plain browser —
 * all accessors fall back gracefully when the SDK isn't present.
 */

// Extend the global Window type minimally — avoids pulling in the full
// @types/telegram-web-app which can drift from reality.
declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        ready(): void
        expand(): void
        close(): void
        initData: string
        initDataUnsafe: { user?: TgUser; start_param?: string }
        colorScheme: 'light' | 'dark'
        themeParams: Record<string, string>
        HapticFeedback?: {
          impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void
          notificationOccurred(type: 'error' | 'success' | 'warning'): void
        }
        MainButton: {
          text: string
          setText(text: string): void
          show(): void
          hide(): void
          onClick(cb: () => void): void
          offClick(cb: () => void): void
          isVisible: boolean
        }
        BackButton: {
          show(): void
          hide(): void
          onClick(cb: () => void): void
          offClick(cb: () => void): void
        }
        openLink?(url: string, options?: { try_instant_view?: boolean }): void
      }
    }
  }
}

export interface TgUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  language_code?: string
  photo_url?: string
}

const tg = () => window.Telegram?.WebApp

export const isInsideTelegram = (): boolean => Boolean(tg()?.initData)

export function ready(): void {
  tg()?.ready()
  tg()?.expand()
}

export function getUser(): TgUser | undefined {
  return tg()?.initDataUnsafe?.user
}

export function getLang(): string {
  return tg()?.initDataUnsafe?.user?.language_code ?? navigator.language.slice(0, 2) ?? 'uk'
}

export function getStartParam(): string {
  return tg()?.initDataUnsafe?.start_param ?? ''
}

export function haptic(type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning' = 'light'): void {
  const fb = tg()?.HapticFeedback
  if (!fb) return
  if (type === 'success' || type === 'error' || type === 'warning') {
    fb.notificationOccurred(type)
  } else {
    fb.impactOccurred(type)
  }
}

export function closeMiniApp(): void {
  tg()?.close()
}

export function showBackButton(onClick: () => void): () => void {
  const bb = tg()?.BackButton
  if (!bb) return () => {}
  try {
    bb.show()
    bb.onClick(onClick)
    return () => {
      try {
        bb.offClick(onClick)
        bb.hide()
      } catch {
        // Ignore errors if Telegram SDK teardown fails
      }
    }
  } catch {
    return () => {}
  }
}

export function openExternalLink(url: string): void {
  const webApp = tg()
  if (webApp?.openLink) {
    webApp.openLink(url)
  } else {
    window.location.href = url
  }
}


